/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ShoppingAssistantOrder } from '../../shoppingAssistant';
import { BoxIcon, TruckIcon, CheckCircleIcon, ClockIcon } from '../icons';

import { useTranslation } from '../../i18n/useTranslation';

interface OrderCardProps {
    order: ShoppingAssistantOrder;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
    const { t } = useTranslation();

    const getStatusIcon = () => {
        switch (order.status) {
            case 'Shipped': return <TruckIcon className="w-5 h-5 text-blue-500" />;
            case 'Delivered': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'Processing': return <ClockIcon className="w-5 h-5 text-orange-500" />;
            default: return <BoxIcon className="w-5 h-5 text-gray-500" />;
        }
    };

    const getStatusText = () => {
        switch (order.status) {
            case 'Shipped': return t('shoppingAssistant_order_status_shipped');
            case 'Delivered': return t('shoppingAssistant_order_status_delivered');
            case 'Processing': return t('shoppingAssistant_order_status_processing');
            default: return order.status;
        }
    };

    const getStatusBG = () => {
        switch (order.status) {
            case 'Shipped': return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300';
            case 'Delivered': return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
            case 'Processing': return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300';
            default: return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm w-full">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getStatusBG()}`}>
                        {getStatusIcon()}
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('shoppingAssistant_order_status_label')}</p>
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">{getStatusText()}</h4>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('shoppingAssistant_order_id_label')}</p>
                    <p className="text-sm font-mono text-indigo-600 dark:text-indigo-400 font-bold">{order.orderId}</p>
                </div>
            </div>

            <div className="space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">{t('shoppingAssistant_order_date_label')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{new Date(order.date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">{t('shoppingAssistant_order_items_label')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{order.items.length} {t('shoppingAssistant_order_items_label').toLowerCase()}</span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">{t('shoppingAssistant_order_total_label')}</span>
                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{order.total}</span>
                </div>
            </div>

            {order.estimatedDelivery && (
                <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex items-center gap-3">
                    <ClockIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <div>
                        <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold uppercase">{t('shoppingAssistant_order_est_delivery_label')}</p>
                        <p className="text-xs font-medium text-gray-900 dark:text-white">{new Date(order.estimatedDelivery).toLocaleDateString()}</p>
                    </div>
                </div>
            )}

            {order.trackingUrl && (
                <button className="w-full mt-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm">
                    {t('shoppingAssistant_order_track_cta')}
                </button>
            )}
        </div>
    );
};
